import { pickImage } from "../../lib/api";
import { useNowSecond } from "../../lib/useNowSecond";

// Tiny isolated subscriber - only this node re-renders each second.
const HudClock = () => {
  const ts = useNowSecond();
  return <span className="font-mono-ui">{ts}</span>;
};

// Reusable CV snapshot - base image + animated bounding boxes + HUD.
// Boxes shape: { x, y, w, h, label, color (lime|amber|red|<hex>), blink, border_style (solid|dotted), confidence? }
export const CvSnapshot = ({
  image,
  camId = "CAM-01",
  boxes = [],
  aspect = "aspect-[16/10]",
  badge,
  showScan = true,
  feature,
}) => {
  const src = image || pickImage(feature);

  return (
    <div
      className={`cv-frame w-full ${aspect}`}
      data-testid={`cv-snapshot-${(camId || "cam").toLowerCase()}`}
    >
      {src ? (
        <img
          src={src}
          alt={camId}
          className="cv-img absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-neutral-900" />
      )}
      <div className="cv-crosshair" />
      {showScan && <div className="cv-scan-line" />}

      <div className="cv-hud top-2 left-2 flex items-center gap-2">
        <span className="font-mono-ui">{camId}</span>
        <span className="opacity-70">|</span>
        <HudClock />
      </div>

      <div className="cv-hud top-2 right-2">
        <span className="cv-live-dot" />LIVE
      </div>

      {badge && (
        <div className="cv-hud bottom-2 left-2 px-2 py-1 bg-black/45 backdrop-blur-sm rounded-sm">
          {badge}
        </div>
      )}

      <div className="cv-hud bottom-2 right-2 opacity-80">v-detect · 0.95</div>

      {boxes.map((b, i) => (
        <CvBox key={i} box={b} index={i} />
      ))}
    </div>
  );
};

const TONE_COLORS = {
  lime: "#84CC16",
  amber: "#F59E0B",
  red: "#EF4444",
  alert: "#EF4444",
};

export const CvBox = ({ box, index = 0 }) => {
  const color = resolveColor(box.color);
  const isLightLabelBg = box.color === "lime" || box.color === "amber";
  const labelText = box.confidence != null && box.confidence > 0
    ? `${box.label}${box.label ? " · " : ""}${box.confidence}%`
    : box.label;
  const isDotted = box.border_style === "dotted";

  return (
    <div
      className={`cv-bbox ${box.blink ? "blink" : ""}`}
      style={{
        left: `${box.x}%`,
        top: `${box.y}%`,
        width: `${box.w}%`,
        height: `${box.h}%`,
        borderColor: color,
        borderStyle: isDotted ? "dashed" : "solid",
        boxShadow: `0 0 0 1px ${hexAlpha(color, 0.25)}, inset 0 0 18px ${hexAlpha(color, 0.08)}`,
        background: hexAlpha(color, 0.06),
        animationDelay: `${index * 90}ms`,
        color,
      }}
    >
      <span className="corner tl" />
      <span className="corner tr" />
      <span className="corner bl" />
      <span className="corner br" />
      {labelText ? (
        <span
          className="cv-label"
          style={{
            background: color,
            color: isLightLabelBg ? "#0A0A0A" : "#FFFFFF",
          }}
        >
          {labelText}
        </span>
      ) : null}
    </div>
  );
};

function resolveColor(raw) {
  if (!raw) return TONE_COLORS.lime;
  if (TONE_COLORS[raw]) return TONE_COLORS[raw];
  if (raw.startsWith("#")) return raw;
  return TONE_COLORS.lime;
}

function hexAlpha(hex, alpha) {
  // hex like #84CC16
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
